import {
  type FilesystemBrowseEntry,
  type KeybindingCommand,
  THREAD_JUMP_KEYBINDING_COMMANDS,
} from "@cleo/contracts";
import type { SidebarThreadSortOrder } from "@cleo/contracts/settings";
import * as Arr from "effect/Array";
import * as Result from "effect/Result";
import { type ReactNode } from "react";
import { sortThreads } from "../lib/threadSort";
import { WORLD_RECORD_STATE_LABEL, worldSessionStates } from "../neucleos/roomRailHidden";
import { worldDisplayName, type NeucleosWorldSummary } from "../neucleos/worldSelection";
import type { WorldWorkspaceBindings } from "../neucleos/worldWorkspaceBinding";
import { normalizeProjectPathForComparison } from "@cleo/client-runtime/state/projects";
import type { WorldViewState } from "../neucleos/worldView";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { type Project, type SidebarThreadSummary, type Thread } from "../types";

export const RECENT_THREAD_LIMIT = 12;
export const ITEM_ICON_CLASS = "size-4 text-icon-muted";
export const ADDON_ICON_CLASS = "size-4";

/**
 * The global search overlay hosts three mutually exclusive surfaces: the
 * command palette (⌘K), the project file picker (⌘P), and project content
 * search (⇧⌘F). One reducer owns open/mode state so the surfaces can never
 * stack and re-triggering a mode's shortcut toggles it closed.
 */
export type SearchOverlayMode = "command" | "files" | "content";

export interface CommandPaletteOpenIntent {
  readonly kind: "add-project" | "new-thread-in";
}

export interface CommandPaletteUiState {
  readonly open: boolean;
  readonly mode: SearchOverlayMode;
  readonly openIntent: CommandPaletteOpenIntent | null;
}

export type CommandPaletteUiAction =
  | { readonly _tag: "SetOpen"; readonly open: boolean }
  | { readonly _tag: "ToggleMode"; readonly mode: SearchOverlayMode }
  | { readonly _tag: "OpenAddProject" }
  | { readonly _tag: "OpenNewThreadIn" }
  | { readonly _tag: "ClearOpenIntent" };

export function reduceCommandPaletteUiState(
  state: CommandPaletteUiState,
  action: CommandPaletteUiAction,
): CommandPaletteUiState {
  switch (action._tag) {
    case "SetOpen":
      return {
        open: action.open,
        mode: "command",
        openIntent: action.open ? state.openIntent : null,
      };
    case "ToggleMode":
      return state.open && state.mode === action.mode
        ? { open: false, mode: "command", openIntent: null }
        : { open: true, mode: action.mode, openIntent: null };
    case "OpenAddProject":
      return { open: true, mode: "command", openIntent: { kind: "add-project" } };
    case "OpenNewThreadIn":
      return { open: true, mode: "command", openIntent: { kind: "new-thread-in" } };
    case "ClearOpenIntent":
      return state.openIntent ? { ...state, openIntent: null } : state;
  }
}

export interface CommandPaletteThreadContentMatch {
  readonly source: "user" | "assistant";
  readonly snippet: string;
  readonly query: string;
}

export interface CommandPaletteItem {
  readonly kind: "action" | "submenu";
  readonly value: string;
  readonly searchTerms: ReadonlyArray<string>;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly threadContentMatch?: CommandPaletteThreadContentMatch;
  readonly timestamp?: string;
  readonly icon: ReactNode;
  readonly disabled?: boolean;
  /** Optional content rendered inline before the title text. */
  readonly titleLeadingContent?: ReactNode;
  /** Optional content rendered inline after the title text (before the timestamp). */
  readonly titleTrailingContent?: ReactNode;
  readonly shortcutCommand?: KeybindingCommand;
}

export interface CommandPaletteActionItem extends CommandPaletteItem {
  readonly kind: "action";
  readonly keepOpen?: boolean;
  readonly run: () => Promise<void>;
}

/** A capability-door refusal is an inert, visible command result, never absence. */
export function buildWorkroomRefusalAction(
  reason: string,
  icon: ReactNode,
): CommandPaletteActionItem {
  return {
    kind: "action",
    value: "action:workrooms-refused",
    searchTerms: ["board", "workroom", "unavailable", "refused"],
    title: "Workrooms unavailable",
    description: reason,
    disabled: true,
    icon,
    run: async () => undefined,
  };
}

export interface CommandPaletteSubmenuItem extends CommandPaletteItem {
  readonly kind: "submenu";
  readonly addonIcon: ReactNode;
  readonly groups: ReadonlyArray<CommandPaletteGroup>;
  readonly initialQuery?: string;
}

export interface CommandPaletteGroup {
  readonly value: string;
  readonly label: string;
  readonly items: ReadonlyArray<CommandPaletteActionItem | CommandPaletteSubmenuItem>;
}

export interface CommandPaletteView {
  readonly addonIcon: ReactNode;
  readonly groups: ReadonlyArray<CommandPaletteGroup>;
  readonly initialQuery?: string;
}

export function enumerateCommandPaletteItems(
  items: ReadonlyArray<CommandPaletteActionItem>,
): CommandPaletteActionItem[] {
  return items.map((item, index) => {
    const shortcutCommand = THREAD_JUMP_KEYBINDING_COMMANDS[index];
    if (shortcutCommand) return { ...item, shortcutCommand };

    const { shortcutCommand: _shortcutCommand, ...itemWithoutShortcut } = item;
    return itemWithoutShortcut;
  });
}

export type CommandPaletteMode = "root" | "root-browse" | "submenu" | "submenu-browse";

export function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function buildProjectActionItems(input: {
  projects: ReadonlyArray<Project>;
  valuePrefix: string;
  icon: (project: Project) => ReactNode;
  runProject: (project: Project) => Promise<void>;
  searchTerms?: (project: Project) => ReadonlyArray<string>;
  shortcutCommand?: KeybindingCommand;
}): CommandPaletteActionItem[] {
  return input.projects.map((project) => ({
    kind: "action",
    value: `${input.valuePrefix}:${project.environmentId}:${project.id}`,
    searchTerms: [project.title, project.workspaceRoot, ...(input.searchTerms?.(project) ?? [])],
    title: project.title,
    description: project.workspaceRoot,
    icon: input.icon(project),
    ...(input.shortcutCommand !== undefined ? { shortcutCommand: input.shortcutCommand } : {}),
    run: async () => {
      await input.runProject(project);
    },
  }));
}

export type ThreadOpenTarget =
  | {
      readonly to: "/session/$sessionId";
      readonly params: { readonly sessionId: string };
      readonly search: { readonly world: string };
    }
  | {
      readonly to: "/$environmentId/$threadId";
      readonly params: { readonly environmentId: string; readonly threadId: string };
    };

/**
 * Where Cmd+K opens a conversation (W-d56f283b). `/session/$sessionId` shows
 * one thread of a Session, its front (`projectSessionExecutions`, the same
 * selection the session view seats), and cannot name another. So only the
 * front thread of a fronted-World Session opens as that Session, exactly as
 * the dock opens it. Every other thread (another thread of the same Session,
 * a Local thread, another World's, one with no Session) keeps its thread
 * route, where the shell addresses that exact thread (resolveRouteConversation).
 */
export function resolveThreadOpenTarget(input: {
  readonly thread: Pick<SidebarThreadSummary, "environmentId" | "id" | "worldKey" | "sessionId">;
  readonly activeWorldKey: string | null;
  /** The fronted World's Session fronts: `projectSessionExecutions(shells, activeWorldKey)`. */
  readonly sessionFronts: ReadonlyMap<string, Pick<SidebarThreadSummary, "environmentId" | "id">>;
}): ThreadOpenTarget {
  const { thread, activeWorldKey } = input;
  const worldKey = thread.worldKey ?? null;
  const sessionId = thread.sessionId ?? null;
  const front = sessionId === null ? undefined : input.sessionFronts.get(sessionId);
  if (
    activeWorldKey !== null &&
    worldKey === activeWorldKey &&
    sessionId !== null &&
    front !== undefined &&
    front.environmentId === thread.environmentId &&
    front.id === thread.id
  ) {
    return { to: "/session/$sessionId", params: { sessionId }, search: { world: worldKey } };
  }
  return {
    to: "/$environmentId/$threadId",
    params: { environmentId: String(thread.environmentId), threadId: String(thread.id) },
  };
}

export type BuildThreadActionItemsThread = Pick<
  SidebarThreadSummary,
  | "archivedAt"
  | "branch"
  | "createdAt"
  | "environmentId"
  | "id"
  | "modelSelection"
  | "projectId"
  | "session"
  | "title"
  | "worktreePath"
> & {
  updatedAt: string;
  latestUserMessageAt?: string | null;
};

export function buildThreadActionItems<TThread extends BuildThreadActionItemsThread>(input: {
  threads: ReadonlyArray<TThread>;
  activeThreadId?: Thread["id"];
  projectTitleById: ReadonlyMap<Project["id"], string>;
  sortOrder: SidebarThreadSortOrder;
  icon: ReactNode;
  /** Optional content rendered inline before the title text per-thread. */
  renderLeadingContent?: (thread: TThread) => ReactNode;
  /** Optional content rendered inline after the title text per-thread. */
  renderTrailingContent?: (thread: TThread) => ReactNode;
  /** Optional rich description (e.g. favicon + workspace icons). Falls back to text. */
  renderDescription?: (thread: TThread, meta: { projectTitle: string | undefined }) => ReactNode;
  getContentMatch?: (thread: TThread) => CommandPaletteThreadContentMatch | undefined;
  runThread: (thread: TThread) => Promise<void>;
  limit?: number;
}): CommandPaletteActionItem[] {
  const sortedThreads = sortThreads(
    input.threads.filter((thread) => thread.archivedAt === null),
    input.sortOrder,
  );
  const visibleThreads =
    input.limit === undefined ? sortedThreads : sortedThreads.slice(0, input.limit);

  return visibleThreads.map((thread) => {
    const projectTitle = input.projectTitleById.get(thread.projectId);
    const descriptionParts: string[] = [];

    if (projectTitle) {
      descriptionParts.push(projectTitle);
    }
    if (thread.branch) {
      descriptionParts.push(`#${thread.branch}`);
    }
    if (thread.id === input.activeThreadId) {
      descriptionParts.push("Current thread");
    }

    const leadingContent = input.renderLeadingContent?.(thread);
    const trailingContent = input.renderTrailingContent?.(thread);
    const contentMatch = input.getContentMatch?.(thread);
    const description = input.renderDescription
      ? input.renderDescription(thread, { projectTitle })
      : descriptionParts.join(` · `);

    return Object.assign(
      {
        kind: "action" as const,
        value: `thread:${thread.id}`,
        searchTerms: [
          thread.title,
          projectTitle ?? ``,
          thread.branch ?? ``,
          contentMatch?.snippet ?? ``,
        ],
        title: thread.title,
        description,
        timestamp: formatRelativeTimeLabel(
          thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt,
        ),
        icon: input.icon,
      },
      leadingContent ? { titleLeadingContent: leadingContent } : {},
      trailingContent ? { titleTrailingContent: trailingContent } : {},
      contentMatch ? { threadContentMatch: contentMatch } : {},
      {
        run: async () => {
          await input.runThread(thread);
        },
      },
    );
  });
}

function rankSearchFieldMatch(field: string, normalizedQuery: string): number {
  const normalizedField = normalizeSearchText(field);
  if (normalizedField.length === 0 || !normalizedField.includes(normalizedQuery)) {
    return Number.NEGATIVE_INFINITY;
  }
  if (normalizedField === normalizedQuery) {
    return 3;
  }
  if (normalizedField.startsWith(normalizedQuery)) {
    return 2;
  }
  return 1;
}

function rankCommandPaletteItemMatch(
  item: CommandPaletteActionItem | CommandPaletteSubmenuItem,
  normalizedQuery: string,
): number {
  const terms = item.searchTerms.filter((term) => term.length > 0);
  if (terms.length === 0) {
    return 0;
  }

  for (const [index, field] of terms.entries()) {
    const fieldRank = rankSearchFieldMatch(field, normalizedQuery);
    if (fieldRank !== Number.NEGATIVE_INFINITY) {
      return 1_000 - index * 100 + fieldRank;
    }
  }

  return 0;
}

export function filterCommandPaletteGroups(input: {
  activeGroups: ReadonlyArray<CommandPaletteGroup>;
  query: string;
  isInSubmenu: boolean;
  projectSearchItems: ReadonlyArray<CommandPaletteActionItem>;
  threadSearchItems: ReadonlyArray<CommandPaletteActionItem>;
}): CommandPaletteGroup[] {
  const isActionsFilter = input.query.startsWith(">");
  const searchQuery = isActionsFilter ? input.query.slice(1) : input.query;
  const normalizedQuery = normalizeSearchText(searchQuery);

  if (normalizedQuery.length === 0) {
    if (isActionsFilter) {
      return input.activeGroups.filter((group) => group.value === "actions");
    }
    return [...input.activeGroups];
  }

  let baseGroups = [...input.activeGroups];
  if (isActionsFilter) {
    baseGroups = baseGroups.filter((group) => group.value === "actions");
  } else if (!input.isInSubmenu) {
    baseGroups = baseGroups.filter((group) => group.value !== "recent-threads");
  }

  const searchableGroups = [...baseGroups];
  if (!input.isInSubmenu && !isActionsFilter) {
    if (input.projectSearchItems.length > 0) {
      searchableGroups.push({
        value: "projects-search",
        label: "Projects",
        items: input.projectSearchItems,
      });
    }
    if (input.threadSearchItems.length > 0) {
      searchableGroups.push({
        value: "threads-search",
        label: "Threads",
        items: input.threadSearchItems,
      });
    }
  }

  return searchableGroups.flatMap((group) => {
    const items = Arr.filterMap(group.items, (item, index) => {
      const haystack = normalizeSearchText(item.searchTerms.join(" "));
      if (!haystack.includes(normalizedQuery)) {
        return Result.failVoid;
      }

      return Result.succeed({
        item,
        index,
        rank: rankCommandPaletteItemMatch(item, normalizedQuery),
      });
    })
      .toSorted((left, right) => right.rank - left.rank || left.index - right.index)
      .map((entry) => entry.item);

    if (items.length === 0) {
      return [];
    }

    return [{ value: group.value, label: group.label, items }];
  });
}

export function buildBrowseGroups(input: {
  browseEntries: ReadonlyArray<FilesystemBrowseEntry>;
  browseQuery: string;
  canBrowseUp: boolean;
  upIcon: ReactNode;
  directoryIcon: ReactNode;
  browseUp: () => void | Promise<void>;
  browseTo: (name: string) => void | Promise<void>;
}): CommandPaletteGroup[] {
  const items: CommandPaletteActionItem[] = [];

  if (input.canBrowseUp) {
    items.push({
      kind: "action",
      value: "browse:up",
      searchTerms: [input.browseQuery, ".."],
      title: "..",
      icon: input.upIcon,
      keepOpen: true,
      run: async () => {
        await input.browseUp();
      },
    });
  }

  for (const entry of input.browseEntries) {
    items.push({
      kind: "action",
      value: `browse:${entry.fullPath}`,
      searchTerms: [input.browseQuery, entry.fullPath, entry.name],
      title: entry.name,
      icon: input.directoryIcon,
      keepOpen: true,
      run: async () => {
        await input.browseTo(entry.name);
      },
    });
  }

  return [{ value: "directories", label: "Directories", items }];
}

export function getCommandPaletteMode(input: {
  currentView: CommandPaletteView | null;
  isBrowsing: boolean;
}): CommandPaletteMode {
  if (input.currentView) {
    return input.isBrowsing ? "submenu-browse" : "submenu";
  }
  return input.isBrowsing ? "root-browse" : "root";
}

/**
 * The palette's doors onto the shell's primary navigation axis: one item per
 * enterable operating mode — every World the person is not currently in,
 * plus the Local door when a World is fronted. Signed out there is nothing
 * to choose (Local is the only mode), matching the World rail's own law.
 */
export function buildWorldDoorItems(input: {
  worlds: ReadonlyArray<NeucleosWorldSummary>;
  activeWorldKey: string | null;
  localSelected: boolean;
  signedIn: boolean;
  worldIcon: ReactNode;
  localIcon: ReactNode;
  enterWorld: (worldKey: string) => void;
  enterLocal: () => void;
}): CommandPaletteActionItem[] {
  if (!input.signedIn) return [];
  const items: CommandPaletteActionItem[] = [];
  for (const world of input.worlds) {
    const current = !input.localSelected && world.worldKey === input.activeWorldKey;
    if (current) continue;
    const name = worldDisplayName(input.worlds, world.worldKey);
    items.push({
      kind: "action",
      value: `world:${world.worldKey}`,
      searchTerms: [name, "world", "enter world", "switch world", "go to world"],
      title: `Enter ${name}`,
      icon: input.worldIcon,
      run: async () => {
        input.enterWorld(world.worldKey);
      },
    });
  }
  if (!input.localSelected) {
    items.push({
      kind: "action",
      value: "world:local",
      searchTerms: ["local", "ungoverned", "operating mode", "continue in local"],
      title: "Continue in Local (ungoverned)",
      icon: input.localIcon,
      run: async () => {
        input.enterLocal();
      },
    });
  }
  return items;
}

/**
 * One item per Session of the fronted World's room, addressed by label and Goal.
 * Reads the room's mirrored state, so a stale mirror (published for a World
 * that is no longer fronted) yields nothing rather than wrong doors.
 * A retired session, or one in an archived room, is still found here — the
 * rail hides them by default (6 Oct 2026) — and its description says so; an
 * archived room's sessions are also found by the room's name.
 */
export function buildWorldSessionItems(input: {
  room: WorldViewState;
  activeWorldKey: string | null;
  localSelected: boolean;
  icon: ReactNode;
  openSession: (sessionId: string) => Promise<void>;
}): CommandPaletteActionItem[] {
  if (input.localSelected || input.room.kind !== "world-ready") return [];
  if (input.activeWorldKey === null || input.room.worldKey !== input.activeWorldKey) return [];
  const { activeSessionId, index } = input.room;
  const roomById = new Map(index.rooms.map((room) => [room.roomId, room]));
  return index.sessions
    .filter((slice) => slice.sessionId !== activeSessionId)
    .map((slice) => {
      const room = roomById.get(slice.roomId);
      const states = worldSessionStates(slice, room).map(
        (recordState) => WORLD_RECORD_STATE_LABEL[recordState],
      );
      const goal = slice.goal?.title ?? "No goal selected";
      return {
        kind: "action" as const,
        value: `slice:${slice.sessionId}`,
        searchTerms: [
          slice.label,
          goal,
          "session",
          "open slice",
          "room",
          ...states,
          ...(room?.archived === true ? [room.label] : []),
        ],
        title: slice.label,
        description: [...states, goal].join(" · "),
        icon: input.icon,
        run: async () => {
          await input.openSession(slice.sessionId);
        },
      };
    });
}

export function buildRootGroups(input: {
  actionItems: ReadonlyArray<CommandPaletteActionItem | CommandPaletteSubmenuItem>;
  recentThreadItems: ReadonlyArray<CommandPaletteActionItem>;
  sessionItems?: ReadonlyArray<CommandPaletteActionItem>;
  worldItems?: ReadonlyArray<CommandPaletteActionItem>;
}): CommandPaletteGroup[] {
  const groups: CommandPaletteGroup[] = [];
  if (input.actionItems.length > 0) {
    groups.push({ value: "actions", label: "Actions", items: input.actionItems });
  }
  // Nearer context first: the fronted World's Sessions, then the other Worlds.
  if (input.sessionItems !== undefined && input.sessionItems.length > 0) {
    groups.push({ value: "sessions", label: "Sessions", items: input.sessionItems });
  }
  if (input.worldItems !== undefined && input.worldItems.length > 0) {
    groups.push({ value: "worlds", label: "Worlds", items: input.worldItems });
  }
  if (input.recentThreadItems.length > 0) {
    groups.push({
      value: "recent-threads",
      label: "Recent Threads",
      items: input.recentThreadItems,
    });
  }
  return groups;
}

/**
 * W-57ca754b: the World the palette speaks for. World mode follows the
 * person's selection, not the room read: a selected World whose rooms are
 * still loading or were refused is still a World (its project commands stay
 * hidden), it is only not ready. Null is Local, the one mode where the
 * project commands mean something.
 */
export interface PaletteWorld {
  readonly worldKey: string | null;
  readonly name: string;
  /** Why a new room cannot be born yet; null once the World's rooms are read. */
  readonly notReadyReason: string | null;
}

export function resolvePaletteWorld(input: {
  readonly room: WorldViewState;
  readonly localSelected: boolean;
  readonly activeWorldKey: string | null;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
}): PaletteWorld | null {
  if (input.localSelected || input.room.kind === "local") return null;
  const worldKey = input.room.worldKey ?? input.activeWorldKey;
  const name =
    input.worlds.find((world) => world.worldKey === worldKey)?.name?.trim() || "this World";
  const notReadyReason =
    input.room.kind === "world-ready"
      ? null
      : input.room.kind === "world-loading"
        ? `Reading ${name}'s rooms…`
        : input.room.reason;
  return { worldKey, name, notReadyReason };
}

/**
 * W-57ca754b: the World a project belongs to, from the actual association,
 * never from its title: this environment's World workspace binding names it
 * (its default execution project, or one of the World's absolute folders is
 * its root), or every one of its threads is an execution of that one World.
 * Null when no World, or more than one, claims it.
 */
export function worldOwningProject(input: {
  readonly project: {
    readonly environmentId: string;
    readonly id: string;
    readonly workspaceRoot: string;
  };
  readonly bindingsByEnvironment: ReadonlyMap<string, WorldWorkspaceBindings>;
  readonly threads: ReadonlyArray<{
    readonly environmentId: string;
    readonly projectId: string;
    readonly worldKey?: string | null | undefined;
  }>;
}): string | null {
  const { project } = input;
  const root = normalizeProjectPathForComparison(project.workspaceRoot);
  const owners = new Set<string>();
  const bindings = input.bindingsByEnvironment.get(project.environmentId) ?? {};
  for (const [worldKey, binding] of Object.entries(bindings)) {
    const folders = [
      ...(binding.sourceFolders?.files ?? []),
      ...(binding.sourceFolders?.codebase ?? []),
      ...(binding.folders ?? []),
      ...(binding.filesRoot ? [binding.filesRoot] : []),
    ].filter((folder) => !folder.startsWith("~"));
    if (
      binding.defaultExecutionProjectId === project.id ||
      folders.some((folder) => normalizeProjectPathForComparison(folder) === root)
    ) {
      owners.add(worldKey);
    }
  }
  if (owners.size === 0) {
    const own = input.threads.filter(
      (thread) => thread.environmentId === project.environmentId && thread.projectId === project.id,
    );
    const keys = new Set(own.map((thread) => thread.worldKey ?? null));
    const [only] = [...keys];
    if (own.length > 0 && keys.size === 1 && typeof only === "string") owners.add(only);
  }
  return owners.size === 1 ? [...owners][0]! : null;
}

/**
 * W-57ca754b: a World's own project whose title is that World's key (its
 * folder was named for the World before the World had a display name) reads
 * as the World, by name. Every other title, including a Local project that
 * merely shares a World key's spelling, is the person's own and stays.
 */
export function personFacingProjectTitle(input: {
  readonly title: string;
  readonly owningWorldKey: string | null;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
}): string {
  if (input.owningWorldKey === null || input.title.trim() !== input.owningWorldKey) {
    return input.title;
  }
  const name = input.worlds.find((world) => world.worldKey === input.owningWorldKey)?.name?.trim();
  return name ? name : input.title;
}

/**
 * W-57ca754b: the palette's code-editor commands that mean nothing in a World.
 * A World's conversations are rooms born in the World (the dock's +), never a
 * thread started in a local project; a World's folders are chosen in Settings →
 * Connections, not added as projects; and project settings (scripts, grouping,
 * checkout) belong to Local projects. These are hidden while a World is selected.
 */
export const LOCAL_ONLY_PALETTE_ACTIONS: ReadonlySet<string> = new Set([
  "action:new-thread",
  "action:new-thread-in",
  "action:add-project",
  "action:project-settings",
]);

/**
 * W-57ca754b: the palette's actions as a selected World reads them, in its own
 * words (rooms, the World by name, files). Null `world` is Local: unchanged.
 * - "New room in <World>" takes the place of "New thread in <project>" and runs
 *   the same birth as the dock's + and the Mac File menu's New Room. While the
 *   World is not ready it is shown disabled, with the reason.
 * - "Go to File" keeps its name (the File menu's own) and still finds files in
 *   the session's folder, which in a World is the World's folder.
 * - "Search Project Contents" searches that same folder by what its files say,
 *   so it is named for files.
 * - Every LOCAL_ONLY_PALETTE_ACTIONS command is hidden.
 */
export function fitActionItemsToWorld(
  items: ReadonlyArray<CommandPaletteActionItem | CommandPaletteSubmenuItem>,
  world:
    | (Pick<PaletteWorld, "name" | "notReadyReason"> & {
        readonly icon: ReactNode;
        readonly newRoom: () => void;
      })
    | null,
): Array<CommandPaletteActionItem | CommandPaletteSubmenuItem> {
  if (world === null) return [...items];
  const newRoom: CommandPaletteActionItem = {
    kind: "action",
    value: "action:new-room",
    searchTerms: ["new room", "room", "conversation", "new thread", "chat", "create", world.name],
    title: `New room in ${world.name}`,
    icon: world.icon,
    ...(world.notReadyReason !== null ? { disabled: true, description: world.notReadyReason } : {}),
    run: async () => {
      if (world.notReadyReason === null) world.newRoom();
    },
  };
  const fitted = items.flatMap(
    (item): Array<CommandPaletteActionItem | CommandPaletteSubmenuItem> => {
      if (LOCAL_ONLY_PALETTE_ACTIONS.has(item.value)) return [];
      if (item.value === "action:search-project-contents") {
        return [
          {
            ...item,
            title: "Search File Contents",
            searchTerms: ["search files", "file contents", "find in files", "grep", "text search"],
          },
        ];
      }
      return [item];
    },
  );
  return [newRoom, ...fitted];
}

/**
 * W-57ca754b: what the native File → New Thread intent (and ⇧⌘O with several
 * projects) does. In a World it is New room, the dock's birth, once the World
 * is ready; before that it opens nothing (the palette's root shows New room
 * disabled with its reason). Only Local opens the project chooser.
 */
export function resolveNewThreadIntent(
  world: Pick<PaletteWorld, "notReadyReason"> | null,
): "project-chooser" | "new-room" | "ignore" {
  if (world === null) return "project-chooser";
  return world.notReadyReason === null ? "new-room" : "ignore";
}

/** W-57ca754b: the content search's own words; in a World it searches files, by the World's name. */
export function contentSearchWords(input: {
  readonly world: Pick<PaletteWorld, "name"> | null;
  readonly projectName: string | null;
}): { readonly title: string; readonly scope: string | null; readonly empty: string } {
  if (input.world !== null) {
    return {
      title: "Search file contents",
      scope: input.world.name,
      empty: `Type to search the files in ${input.world.name}.`,
    };
  }
  return {
    title: "Search project contents",
    scope: input.projectName,
    empty: "Type to search across your project.",
  };
}

export function getCommandPaletteInputPlaceholder(
  mode: CommandPaletteMode,
  inWorld = false,
): string {
  switch (mode) {
    case "root":
      return inWorld
        ? "Search commands, sessions, and threads..."
        : "Search commands, projects, and threads...";
    case "root-browse":
      return "Enter project path (e.g. ~/projects/my-app)";
    case "submenu":
      return "Search...";
    case "submenu-browse":
      return "Enter path (e.g. ~/projects/my-app)";
  }
}
